import type { BoardroomListCard } from '../../../types';
import { useContext } from 'react';
import TagChip from '../TagChip';
import { TableFilterContext, filterWords, textMatches } from './tableFilter';

/**
 * A table the room was asked for.
 *
 * Every other report in this folder is a shape somebody anticipated — a
 * competitor roster, a price map, a pain gap — each one a builder plus a
 * renderer, so "the skate brands with their prices, cheapest first" meant
 * writing code. This is the one whose QUERY is on the card: the chat writes the
 * spec from what a person asked for, and this draws whatever comes back.
 *
 * So it renders COLUMNS IT HAS NEVER HEARD OF. There is no switch on column
 * names here on purpose — the server decides which columns exist, and a
 * renderer that knew their names would be a second place to keep in step.
 *
 * A BLANK CELL IS A FINDING. It means nobody recorded that, which is the room
 * saying "nobody has looked" — worth reading, and never worth filling in with a
 * dash that looks like a measurement. The em dash below is for the reader's
 * eye; the cell itself stays empty.
 */

/** A cell that is a web address, whatever its column is called. Reports can ask
 *  for any field (`product.url`, a quote's source), so the column name is no
 *  longer a reliable sign of a link — the value is. */
export const isLink = (value: string) => /^https?:\/\/\S+$/.test(value.trim());

/** Cells that read better as a link than as text. Not a per-column renderer —
 *  one rule, applied to whatever comes back that is a web address. */
function Cell({ value }: { column: string; value: string }) {
  if (!value) {
    return <span className="text-gray-300 dark:text-gray-600">-</span>;
  }
  if (isLink(value)) {
    return (
      // A WIDTH, OR IT IS NOT SHORTENED. An address has no spaces, so without
      // a cap the browser made its column as wide as the whole address and
      // squeezed every other column to a word per line.
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        title={value}
        className="inline-block max-w-[14rem] truncate align-bottom text-indigo-600 hover:underline dark:text-indigo-400"
      >
        {value.replace(/^https?:\/\/(www\.)?/, '')}
      </a>
    );
  }
  return <span>{value}</span>;
}

/** A row's tags, as the room's one tag badge. Clicking one puts it in the
 *  lens, or takes it out; the table, fetched under the lens, narrows to match. */
function TagBadges({
  tags,
  lensTags,
  onPickTag,
}: {
  tags: { dimension: string; value: string }[];
  lensTags?: string[];
  onPickTag?: (key: string) => void;
}) {
  if (!tags.length) return <span className="text-gray-300 dark:text-gray-600">-</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {tags.map(t => {
        const key = `${t.dimension}:${t.value}`;
        return (
          <TagChip
            key={key}
            dimension={t.dimension}
            value={t.value}
            picked={lensTags?.includes(key) ?? false}
            onPick={onPickTag ? () => onPickTag(key) : undefined}
            testId={onPickTag ? 'list-tag' : undefined}
          />
        );
      })}
    </span>
  );
}

/** Numbers line up; words do not. */
const NUMERIC = new Set(['products', 'pains', 'evidence', 'sources', 'volume']);

export default function ListCardReport({
  data,
  limit,
  onOpen,
  onPickTag,
  lensTags,
}: {
  data: BoardroomListCard;
  /** A tag badge clicked. Without it the badges are not buttons. */
  onPickTag?: (key: string) => void;
  /** The tags in the lens now, so a badge in it looks picked. */
  lensTags?: string[];
  /** Card density shows a handful; the full view shows the table. */
  limit?: number;
  /** Open the item this row is. */
  onOpen?: (id: number) => void;
}) {
  // THE ROOM'S FILTER. Drawn here rather than by `DataTable`, so this table
  // reads it itself, against every cell a row has.
  const filterText = useContext(TableFilterContext);
  const words = filterWords(filterText);
  const kept = words.length
    ? data.rows.filter(r => textMatches(Object.values(r.cells).join(' '), words))
    : data.rows;
  const rows = limit ? kept.slice(0, limit) : kept;
  // A column of numbers lines up on the right, whatever it is called: a report
  // can ask for any field, so the fixed list alone no longer covers them.
  const numeric = new Set(
    data.columns
      .filter(
        c =>
          NUMERIC.has(c.key) ||
          (rows.some(r => r.cells[c.key]) &&
            rows.every(r => !r.cells[c.key] || /^-?[\d,]+(\.\d+)?$/.test(r.cells[c.key]))),
      )
      .map(c => c.key),
  );

  if (words.length && !kept.length && data.rows.length) {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400" data-testid="filter-empty">
        Nothing in this table matches “{filterText.trim()}”.
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400">
        Nothing on the board matches this yet. It draws what the room has already
        found - if you want it filled, the room has to go and look.
      </div>
    );
  }

  return (
    <div className="min-w-0">
      {!limit && (
        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
          {data.total.toLocaleString()} row{data.total === 1 ? '' : 's'}
          {words.length
            ? `, ${kept.length} match “${filterText.trim()}”`
            : data.total > rows.length
              ? `, showing ${rows.length}`
              : ''}
          {data.scoped ? ' · cut by the lens you have on' : ''}
        </p>
      )}
      {/* THE TABLE SCROLLS SIDEWAYS ON ITS OWN when it must, so the count
          above it and the card around it stay put: a wide table used to slide
          the whole card body and cut the names off on the left. */}
      <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
          <tr>
            {data.columns.map(c => (
              <th
                key={c.key}
                title={c.hint}
                className={`py-1 font-normal ${
                  numeric.has(c.key) ? 'text-right' : 'text-left'
                }`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr
              key={r.id}
              className="border-t border-gray-100 align-top dark:border-gray-700/60"
            >
              {data.columns.map((c, i) => (
                <td
                  key={c.key}
                  // ROOM TO READ: words wrap inside a column with a floor and a
                  // ceiling, so no one column takes the table and none is left
                  // a word wide. The name gets the most.
                  className={`py-1 pr-2 ${
                    numeric.has(c.key)
                      ? 'whitespace-nowrap text-right tabular-nums text-gray-600 dark:text-gray-300'
                      : `${i === 0 ? 'min-w-[9rem]' : c.key === 'tags' ? 'min-w-[11rem]' : 'min-w-[6rem]'} max-w-[22rem] break-words text-gray-700 dark:text-gray-200`
                  }`}
                >
                  {/* The first column is the row's name, so it is the handle
                      for opening the thing itself. */}
                  {i === 0 && onOpen ? (
                    <button
                      type="button"
                      onClick={() => onOpen(r.id)}
                      className="max-w-full truncate text-left hover:underline"
                    >
                      <Cell column={c.key} value={r.cells[c.key] ?? ''} />
                    </button>
                  ) : c.key === 'tags' && r.tags ? (
                    <TagBadges tags={r.tags} lensTags={lensTags} onPickTag={onPickTag} />
                  ) : (
                    <Cell column={c.key} value={r.cells[c.key] ?? ''} />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}
